// Per-disease, rule-based inheritance-pattern detection. Each detector is pure
// (TokenisedPedigree + diseaseId + per-disease context → PatternResult).
//
// TGCLOUD-1712 W3.1–W3.5. Four things changed about what this file claims:
//
//  W3.1 (DR-1) — a Mendelian mode may only be ASSERTED for a disease the
//    reference data (or the clinician) says it is meaningful for. The gate lives
//    in `analysis/diseaseMeta.js`; `inheritanceAssertionGate` below wraps it with
//    the feature flag, and when it denies, `detectInheritancePatterns` returns an
//    EMPTY array. `analysis/index.js` turns that denial into the familial
//    aggregation output — a different statement, not a weaker pattern.
//
//  W3.2 (DR-3) — no criterion is satisfied by mere absence any more. "No
//    male-to-male transmission", "no paternal transmission", "only males
//    affected" and "unaffected parents" now each require the disconfirming
//    observation to have been POSSIBLE in this pedigree, and a sex-limited
//    disease is excluded from sex-linkage inference altogether, because for it
//    "no affected females" is a fact about the disease rather than the family.
//
//  W3.3 (DR-2) — the bare Strong/Moderate grade is no longer the message. Every
//    result carries `evidence`: what was observed (with counts and the specific
//    configurations) and the limitation of that observation. `confidence` stays
//    on the object for internal consumers (`cascadePriors` reads it); it is not
//    what the panel should show.
//
//  W3.4/W3.5 — molecular upgrades need a real gene↔disease link and a heritable
//    variant; generations are counted as generations, not as parent-child pairs;
//    consanguinity is scoped to the affected lineage; placeholders are excluded.
//
// PatternResult = {
//   id, diseaseId, confidence, supportingIds, rationale[],
//   evidence: { statement, limitation, observations[], limitations[], observability },
//   ruledOut, ruledOutReason, assertable, notAssertableReason
// }

import { PATTERN, CONFIDENCE } from '../constants';
import {
  canAssertMendelianMode,
  sexLimitedTo,
  OUTPUT_KIND,
  NO_MODE_REASON
} from '../diseaseMeta';
import {
  affectedList,
  affectedMales,
  affectedFemales,
  pidsOf,
  verticalPairs,
  affectedChainGenerations,
  affectedWithBothParentsAffected,
  maternalTransmissionPairs,
  hasMaleToMaleTransmission,
  hasPaternalTransmission,
  bothSexesAffected,
  affectedWithUnaffectedParents,
  affectedWithAscertainedUnaffectedParents,
  affectedSibshipsWithUnaffectedParents,
  maleToMaleObservability,
  paternalTransmissionObservability,
  affectedFemaleObservability,
  carrierLinkingFemales,
  affectedFatherDaughterSignature,
  consanguineousLoopsInAffectedAncestry,
  hasConsanguinity,
  diseaseAssociatedPathogenicGenes
} from './inheritance.helpers';

// Why a pattern could not be assessed at all (distinct from ruled out, which is
// a positive finding, and from `none`, which means there is nothing to assess).
export const NOT_ASSERTABLE = Object.freeze({
  sexLimitedDisease: 'sex_limited_disease'
});

// Stable keys for every observation and limitation the engine can emit, so the
// panel can translate them and a reviewer can enumerate them. The English text
// travels with the result; the key is what UI and tests should match on.
export const EVIDENCE_KEY = Object.freeze({
  affectedCount: 'affected_count',
  bothParentsAffected: 'both_parents_affected',
  bothSexesAffected: 'both_sexes_affected',
  carrierLinkedMales: 'carrier_linked_affected_males',
  consanguinityInLineage: 'consanguinity_in_affected_lineage',
  consanguinityElsewhere: 'consanguinity_outside_affected_lineage',
  fatherToDaughters: 'affected_father_to_all_daughters',
  maleToMaleAbsent: 'male_to_male_absent_and_observable',
  maleToMaleNotObservable: 'male_to_male_not_observable',
  maleToMalePresent: 'male_to_male_present',
  maternalLine: 'maternal_line_transmission',
  molecularSupport: 'molecular_support',
  molecularSomatic: 'molecular_excluded_somatic',
  molecularUnassociated: 'molecular_excluded_unassociated',
  noAffectedFemales: 'no_affected_females_and_observable',
  affectedFemaleNotObservable: 'affected_female_not_observable',
  affectedFemalesPresent: 'affected_females_present',
  noCarrierLink: 'no_carrier_link_between_affected_males',
  noTransmission: 'no_transmission_observed',
  nuclearVariant: 'nuclear_pathogenic_variant',
  onlyMalesAffected: 'only_males_affected_and_observable',
  parentsNotAscertained: 'unaffected_parents_not_ascertained',
  paternalAbsent: 'paternal_transmission_absent_and_observable',
  paternalNotObservable: 'paternal_transmission_not_observable',
  paternalPresent: 'paternal_transmission_present',
  sexLimited: 'sex_limited_disease',
  noSegregation: 'no_transmission_between_affected',
  singleMaternalPair: 'single_maternal_transmission_only',
  singleTransmission: 'single_transmission_only',
  unaffectedParents: 'affected_with_unaffected_parents',
  verticalChain: 'vertical_chain_across_generations'
});

const obs = (key, text, extra = {}) => ({ key, text, ...extra });

// Ordering contract: `observations` then `limitations`, each MOST IMPORTANT
// FIRST, and `rationale` is their flattened text in that order. The panel is
// expected to render all of it (the old one-line truncation is being removed
// separately) — so nothing here may rely on being the only line shown.
function patternResult({
  id,
  diseaseId,
  confidence,
  supportingIds = [],
  observations = [],
  limitations = [],
  observability = {},
  ruledOut = false,
  ruledOutReason = null,
  assertable = true,
  notAssertableReason = null
}) {
  return {
    id,
    diseaseId,
    confidence,
    supportingIds,
    rationale: [...observations, ...limitations].map((e) => e.text),
    evidence: {
      // What was seen, and what that observation cannot settle — the two things
      // DR-2 puts on the card face in place of a strength word.
      statement: observations.length ? observations[0].text : null,
      limitation: limitations.length ? limitations[0].text : null,
      observations,
      limitations,
      // Which absence-based criteria were checkable here. Recorded so a reviewer
      // can see that an absence was counted only where it was observable.
      observability
    },
    ruledOut,
    ruledOutReason,
    assertable,
    notAssertableReason
  };
}

const empty = (id, diseaseId) =>
  patternResult({ id, diseaseId, confidence: CONFIDENCE.none });

const notAssertable = (id, diseaseId, reason, text) =>
  patternResult({
    id,
    diseaseId,
    confidence: CONFIDENCE.none,
    assertable: false,
    notAssertableReason: reason,
    limitations: [obs(EVIDENCE_KEY.sexLimited, text)]
  });

// ---------------------------------------------------------------------------
// Per-disease context
// ---------------------------------------------------------------------------

/**
 * Normalise the third argument.
 *
 * The shape is `{ meta, gateEnabled }`. A bare DiseaseMeta is also accepted
 * (recognised by its own keys) so a mis-shaped call APPLIES the metadata rather
 * than silently ignoring it — applying it is the conservative direction, since
 * the metadata only ever removes claims.
 */
function toContext(context) {
  if (!context) return { meta: null, gateEnabled: false };
  if ('meta' in context || 'gateEnabled' in context)
    return {
      meta: context.meta || null,
      gateEnabled: context.gateEnabled === true
    };
  const looksLikeMeta =
    'assertion' in context ||
    'safeToAssertMendelian' in context ||
    'clinicianMode' in context ||
    'associatedGenes' in context;
  return { meta: looksLikeMeta ? context : null, gateEnabled: false };
}

/**
 * May this disease carry a Mendelian mode at all, and if not, why not?
 *
 * This is the helper `analysis/index.js` calls to decide between the inheritance
 * card and the familial-aggregation card. It wraps `canAssertMendelianMode` with
 * the feature flag so there is one place that knows the flag's meaning.
 *
 * @param {object|null} meta per-disease metadata (see `toDiseaseMeta`)
 * @param {{ gateEnabled?: boolean }} [options] `gateEnabled` is the
 *   `inheritanceCandidateGate` flag. FALSE (default) = the gate is not applied
 *   and every disease is admitted, as today. The other W3 corrections are NOT
 *   flagged — they apply either way.
 * @returns {{
 *   allowed: boolean,
 *   reason: string|null,     // NO_MODE_REASON.* when denied
 *   source: string,          // which layer decided
 *   gateEnabled: boolean,
 *   outputKind: string,      // OUTPUT_KIND.*
 *   sexLimitedTo: 'M'|'F'|null
 * }}
 */
export function inheritanceAssertionGate(meta, options = {}) {
  const gateEnabled = options.gateEnabled === true;
  const limitedTo = sexLimitedTo(meta);

  if (!gateEnabled)
    return {
      allowed: true,
      reason: null,
      source: 'gate_disabled',
      gateEnabled: false,
      outputKind: OUTPUT_KIND.inheritancePattern,
      sexLimitedTo: limitedTo
    };

  const decision = canAssertMendelianMode(meta);
  return {
    allowed: decision.allowed,
    reason: decision.reason,
    source: decision.source,
    gateEnabled: true,
    outputKind: decision.allowed
      ? OUTPUT_KIND.inheritancePattern
      : OUTPUT_KIND.familialAggregation,
    sexLimitedTo: limitedTo
  };
}

// Re-exported so a caller that only needs the reason vocabulary does not have to
// import two modules.
export { NO_MODE_REASON, OUTPUT_KIND };

// Shared limitation: how much evidence there is at all. Two affected relatives
// is the configuration that used to read "Strong".
function scaleLimitation(affected, chainGenerations) {
  if (affected.length > 2 && chainGenerations > 2) return null;
  if (chainGenerations <= 1)
    return obs(
      EVIDENCE_KEY.noSegregation,
      `${affected.length} affected relative(s) with no parent-to-child transmission recorded between them; insufficient to establish segregation`
    );
  return obs(
    EVIDENCE_KEY.affectedCount,
    `${affected.length} affected relative(s) across ${chainGenerations} generations; a larger pedigree would be needed to establish segregation`
  );
}

// Shared molecular limitations, in the two ways a variant can fail to license a
// Mendelian claim.
function molecularLimitations(mol) {
  const out = [];
  if (mol.somaticExcluded.length)
    out.push(
      obs(
        EVIDENCE_KEY.molecularSomatic,
        `Pathogenic ${mol.somaticExcluded.join(
          ', '
        )} variant is recorded as somatic: acquired, so not evidence of inherited transmission`
      )
    );
  if (mol.unassociatedExcluded.length)
    out.push(
      obs(
        EVIDENCE_KEY.molecularUnassociated,
        `Pathogenic ${mol.unassociatedExcluded.join(
          ', '
        )} variant recorded in an affected individual is not associated with this diagnosis, so it does not support a mode for it`
      )
    );
  return out;
}

// ---------------------------------------------------------------------------
// Autosomal dominant
// ---------------------------------------------------------------------------
function detectAutosomalDominant(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.AD, diseaseId);

  const chain = affectedChainGenerations(ped, diseaseId);
  const pairs = verticalPairs(ped, diseaseId);
  const m2m = hasMaleToMaleTransmission(ped, diseaseId);
  const bothSexes = bothSexesAffected(ped, diseaseId);
  const mol = diseaseAssociatedPathogenicGenes(ped, diseaseId, meta);

  const observations = [];
  const limitations = [];

  // Molecular support leads when it is real: an associated, heritable
  // pathogenic variant in affected individuals, with the disease transmitting.
  const molecularSupports = mol.genes.length > 0 && chain >= 2;
  if (molecularSupports)
    observations.push(
      obs(
        EVIDENCE_KEY.molecularSupport,
        `Pathogenic ${mol.genes.join(
          ', '
        )} variant (associated with this diagnosis) segregating with affected individuals`,
        { genes: mol.genes }
      )
    );

  // PA-1: generations spanned by a chain of affected individuals, not a count
  // of parent-child pairs. One affected parent with two affected children is
  // two pairs but one transmission each and two generations.
  if (chain >= 3)
    observations.push(
      obs(
        EVIDENCE_KEY.verticalChain,
        `Affected individuals in ${chain} successive generations (vertical transmission)`,
        { generations: chain, transmissions: pairs }
      )
    );
  else if (chain === 2)
    observations.push(
      obs(
        EVIDENCE_KEY.verticalChain,
        `Affected parent and affected child in ${pairs} transmission(s), spanning 2 generations`,
        { generations: chain, transmissions: pairs }
      )
    );

  if (m2m)
    observations.push(
      obs(
        EVIDENCE_KEY.maleToMalePresent,
        'Male-to-male transmission observed (excludes X-linked and mitochondrial inheritance)'
      )
    );

  if (bothSexes)
    observations.push(
      obs(
        EVIDENCE_KEY.bothSexesAffected,
        `Both sexes affected (${affectedMales(ped, diseaseId).length} male, ${
          affectedFemales(ped, diseaseId).length
        } female)`
      )
    );

  let confidence;
  if (chain >= 3 || (chain >= 2 && m2m)) confidence = CONFIDENCE.strong;
  else if (chain >= 2) confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;
  if (molecularSupports) confidence = CONFIDENCE.strong;

  if (chain < 2)
    limitations.push(
      obs(
        EVIDENCE_KEY.noTransmission,
        'No parent-to-child transmission of this diagnosis is recorded'
      )
    );
  else if (chain === 2)
    limitations.push(
      obs(
        EVIDENCE_KEY.singleTransmission,
        'Transmission spans two generations only; a third would be needed to demonstrate segregation through the family'
      )
    );

  const scale = scaleLimitation(affected, chain);
  if (scale) limitations.push(scale);
  limitations.push(...molecularLimitations(mol));

  return patternResult({
    id: PATTERN.AD,
    diseaseId,
    confidence,
    supportingIds: pidsOf(affected),
    observations,
    limitations,
    observability: { generationsSpanned: chain, transmissionEvents: pairs }
  });
}

// ---------------------------------------------------------------------------
// Autosomal recessive
// ---------------------------------------------------------------------------
function detectAutosomalRecessive(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.AR, diseaseId);

  const chain = affectedChainGenerations(ped, diseaseId);
  const awup = affectedWithUnaffectedParents(ped, diseaseId);
  const ascertained = affectedWithAscertainedUnaffectedParents(ped, diseaseId);
  const sibships = affectedSibshipsWithUnaffectedParents(ped, diseaseId);
  const ascertainedSibships = sibships.filter((s) => s.parentsAscertained);
  const pseudodominant = affectedWithBothParentsAffected(ped, diseaseId);
  const lineageLoops = consanguineousLoopsInAffectedAncestry(ped, diseaseId);
  const mol = diseaseAssociatedPathogenicGenes(ped, diseaseId, meta);

  // Ruled out only by a genuine multi-generation chain. `verticalPairs >= 2`
  // used to do this, and it ruled out AR for the single most characteristic
  // recessive configuration there is: two affected parents of one affected
  // child. That is two pairs, two generations, and it is pseudodominance —
  // exactly what recessive inheritance predicts from an affected × affected
  // mating (PA-1).
  //
  // And not even then inside a consanguineous lineage: repeated recessive
  // segregation down a consanguineous line looks vertical, so a rule-out there
  // would hide the very families where recessive inheritance is most likely.
  // Ruling a mode out removes it from the panel entirely, so the fail-safe
  // direction is to keep it and state the tension.
  if (chain >= 3 && !lineageLoops.length) {
    return patternResult({
      id: PATTERN.AR,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.verticalChain,
          `Affected individuals in ${chain} successive generations; sustained vertical transmission is not expected under simple recessive inheritance`,
          { generations: chain }
        )
      ],
      ruledOut: true,
      ruledOutReason: 'vertical_transmission',
      observability: { generationsSpanned: chain }
    });
  }

  const observations = [];
  const limitations = [];

  if (ascertainedSibships.length)
    observations.push(
      obs(
        EVIDENCE_KEY.unaffectedParents,
        `${ascertainedSibships.length} sibship(s) with multiple affected siblings whose assessed parents are unaffected`,
        { sibships: ascertainedSibships }
      )
    );
  else if (sibships.length)
    observations.push(
      obs(
        EVIDENCE_KEY.unaffectedParents,
        `${sibships.length} sibship(s) with multiple affected siblings and no affected parent`,
        { sibships }
      )
    );

  if (pseudodominant.length)
    observations.push(
      obs(
        EVIDENCE_KEY.bothParentsAffected,
        `${pseudodominant.length} affected individual(s) with two affected parents (pseudodominance — expected under recessive inheritance, not evidence against it)`,
        { pids: pidsOf(pseudodominant) }
      )
    );

  if (awup.length && !sibships.length)
    observations.push(
      obs(
        EVIDENCE_KEY.unaffectedParents,
        `${awup.length} affected individual(s) whose recorded parents are unaffected`,
        { pids: pidsOf(awup) }
      )
    );

  // PA-6: only unions in the affected individuals' own ancestry.
  if (lineageLoops.length)
    observations.push(
      obs(
        EVIDENCE_KEY.consanguinityInLineage,
        `Consanguineous union in the ancestry of affected individual(s) (${lineageLoops
          .map((l) => l.partners.join(' × '))
          .join('; ')}) — elevates the recessive prior`,
        { loops: lineageLoops }
      )
    );

  // Kept rather than ruled out (see above) — but the tension is stated.
  if (chain >= 3)
    limitations.push(
      obs(
        EVIDENCE_KEY.verticalChain,
        `Affected individuals in ${chain} successive generations, which usually argues against recessive inheritance — retained here only because the affected lineage is consanguineous, where repeated recessive segregation can look vertical`,
        { generations: chain }
      )
    );

  // "Unaffected parents" is the load-bearing recessive criterion, and in this
  // data model it means only that no diagnosis was entered for them. Say so
  // when nothing was actually assessed.
  if (awup.length && !ascertained.length)
    limitations.push(
      obs(
        EVIDENCE_KEY.parentsNotAscertained,
        'The parents carry no recorded diagnosis, phenotype or test result: "unaffected parents" here is an absence of records, not an assessment'
      )
    );

  // PA-6 made visible: a cousin marriage in another branch is not evidence
  // about this diagnosis, and the panel should say why it was not counted.
  if (!lineageLoops.length && hasConsanguinity(ped))
    limitations.push(
      obs(
        EVIDENCE_KEY.consanguinityElsewhere,
        'A consanguineous union exists in this pedigree but not in the ancestry of the affected individuals, so it does not raise the recessive prior for this diagnosis'
      )
    );

  if (!awup.length && !sibships.length && !pseudodominant.length)
    limitations.push(
      obs(
        EVIDENCE_KEY.noTransmission,
        'Parental status does not indicate recessive inheritance'
      )
    );

  const scale = scaleLimitation(affected, chain);
  if (scale) limitations.push(scale);

  // Molecular evidence is reported against AR as a competing explanation, not
  // as an exclusion. A pathogenic allele transmitting with the disease across
  // two generations is equally pseudodominance, so ruling AR out on it repeats
  // the PA-1 error in molecular clothing.
  if (mol.genes.length && chain >= 2)
    limitations.push(
      obs(
        EVIDENCE_KEY.molecularSupport,
        `A pathogenic ${mol.genes.join(
          ', '
        )} variant transmits with the diagnosis, which is more consistent with dominant inheritance — though an affected parent of an affected child is also expected under recessive pseudodominance`
      )
    );
  limitations.push(...molecularLimitations(mol));

  let confidence;
  if (
    (ascertainedSibships.length > 0 && ascertained.length > 0) ||
    (ascertained.length > 0 && lineageLoops.length > 0) ||
    (awup.length > 0 && lineageLoops.length > 0)
  )
    confidence = CONFIDENCE.strong;
  else if (awup.length > 0 || sibships.length > 0 || pseudodominant.length > 0)
    confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;

  return patternResult({
    id: PATTERN.AR,
    diseaseId,
    confidence,
    supportingIds: pidsOf(awup.length ? awup : affected),
    observations,
    limitations,
    observability: {
      generationsSpanned: chain,
      parentalPhenotypeAscertained: ascertained.length > 0,
      consanguinityInAffectedLineage: lineageLoops.length > 0
    }
  });
}

// ---------------------------------------------------------------------------
// X-linked recessive
// ---------------------------------------------------------------------------
function detectXLinkedRecessive(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.XLR, diseaseId);

  // DR-3, the residual case the structural check cannot reach. For a disease
  // that can only affect one sex, every sex-linkage criterion this engine has
  // is predetermined: "no affected females" is guaranteed, and a maternal
  // carrier line cannot be distinguished from any other. Prostate cancer with
  // two affected males reached "X-linked recessive, strong" on exactly that.
  const limitedTo = sexLimitedTo(meta);
  if (limitedTo)
    return notAssertable(
      PATTERN.XLR,
      diseaseId,
      NOT_ASSERTABLE.sexLimitedDisease,
      `This diagnosis affects ${
        limitedTo === 'M' ? 'males' : 'females'
      } only, so the sex distribution of affected relatives cannot indicate X-linked inheritance`
    );

  if (hasMaleToMaleTransmission(ped, diseaseId))
    return patternResult({
      id: PATTERN.XLR,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.maleToMalePresent,
          'Male-to-male transmission observed, which excludes X-linked inheritance'
        )
      ],
      ruledOut: true,
      ruledOutReason: 'male_to_male_transmission'
    });

  const males = affectedMales(ped, diseaseId);
  const females = affectedFemales(ped, diseaseId);
  const m2mObs = maleToMaleObservability(ped, diseaseId);
  const femaleObs = affectedFemaleObservability(ped, diseaseId);
  const carrierLinks = carrierLinkingFemales(ped, diseaseId);

  const observations = [];
  const limitations = [];

  // The positive evidence X-linked recessive actually rests on. Two affected
  // males and no father-son pair is not it: that was satisfied by two men who
  // merely shared a diagnosis.
  if (carrierLinks.length)
    observations.push(
      obs(
        EVIDENCE_KEY.carrierLinkedMales,
        `Affected males linked through an unaffected female (${carrierLinks
          .map((c) => `${c.pid} links ${c.linkedPids.join(', ')}`)
          .join('; ')}), the carrier-transmission configuration`,
        { links: carrierLinks }
      )
    );

  observations.push(
    obs(EVIDENCE_KEY.affectedCount, `${males.length} affected male(s)`, {
      pids: pidsOf(males)
    })
  );

  // The absence-based criteria — each stated only where the disconfirming
  // observation was structurally possible here.
  if (m2mObs.observable)
    observations.push(
      obs(
        EVIDENCE_KEY.maleToMaleAbsent,
        `No male-to-male transmission, in a pedigree that could have shown it (${m2mObs.affectedMalesWithRecordedFather.length} affected male(s) with a recorded father, ${m2mObs.affectedMalesWithRecordedSons.length} with recorded sons)`
      )
    );
  else
    limitations.push(
      obs(
        EVIDENCE_KEY.maleToMaleNotObservable,
        'Male-to-male transmission could not have been observed here (no affected male has a recorded father or recorded sons), so its absence is not evidence'
      )
    );

  if (females.length === 0 && femaleObs.observable)
    observations.push(
      obs(
        EVIDENCE_KEY.noAffectedFemales,
        `No affected females, among ${femaleObs.unaffectedFemaleRelatives.length} recorded female relative(s) of the affected individuals`
      )
    );
  else if (females.length === 0)
    limitations.push(
      obs(
        EVIDENCE_KEY.affectedFemaleNotObservable,
        'No female relatives of the affected individuals are recorded, so the absence of affected females is not evidence'
      )
    );

  if (females.length)
    limitations.push(
      obs(
        EVIDENCE_KEY.affectedFemalesPresent,
        `${females.length} affected female(s) present — atypical for X-linked recessive inheritance`
      )
    );

  if (!carrierLinks.length)
    limitations.push(
      obs(
        EVIDENCE_KEY.noCarrierLink,
        'The affected males are not linked to each other through an unaffected female, so carrier transmission is not demonstrated'
      )
    );

  // Every tier is decided on signals that were actually OBSERVABLE. The two
  // absences count only where they could have gone the other way — "no affected
  // females" is a signal only if there were females who could have been
  // affected, and male-to-male transmission is ruled out above, so its absence
  // here is only a signal where the pedigree could have shown it.
  const affectedFemalesPresent = females.length > 0;
  const noAffectedFemalesObserved =
    !affectedFemalesPresent && femaleObs.observable;
  const observedSignals =
    (carrierLinks.length ? 1 : 0) +
    (m2mObs.observable ? 1 : 0) +
    (noAffectedFemalesObserved ? 1 : 0);

  let confidence;
  if (
    males.length >= 2 &&
    carrierLinks.length >= 1 &&
    m2mObs.observable &&
    noAffectedFemalesObserved
  )
    confidence = CONFIDENCE.strong;
  else if (males.length >= 1 && !affectedFemalesPresent && observedSignals >= 1)
    confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;

  const scale = scaleLimitation(
    affected,
    affectedChainGenerations(ped, diseaseId)
  );
  if (scale) limitations.push(scale);

  return patternResult({
    id: PATTERN.XLR,
    diseaseId,
    confidence,
    supportingIds: pidsOf(males.length ? males : affected),
    observations,
    limitations,
    observability: {
      maleToMale: m2mObs.observable ? 'absent_observable' : 'not_observable',
      affectedFemale: femaleObs.observable
        ? females.length
          ? 'observed'
          : 'absent_observable'
        : 'not_observable',
      carrierLinkedMales: carrierLinks.length
    }
  });
}

// ---------------------------------------------------------------------------
// X-linked dominant
// ---------------------------------------------------------------------------
function detectXLinkedDominant(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.XLD, diseaseId);

  const limitedTo = sexLimitedTo(meta);
  if (limitedTo)
    return notAssertable(
      PATTERN.XLD,
      diseaseId,
      NOT_ASSERTABLE.sexLimitedDisease,
      `This diagnosis affects ${
        limitedTo === 'M' ? 'males' : 'females'
      } only, so the sex distribution of affected relatives cannot indicate X-linked inheritance`
    );

  if (hasMaleToMaleTransmission(ped, diseaseId))
    return patternResult({
      id: PATTERN.XLD,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.maleToMalePresent,
          'Male-to-male transmission observed, which excludes X-linked inheritance'
        )
      ],
      ruledOut: true,
      ruledOutReason: 'male_to_male_transmission'
    });

  const chain = affectedChainGenerations(ped, diseaseId);
  const bothSexes = bothSexesAffected(ped, diseaseId);
  const m2mObs = maleToMaleObservability(ped, diseaseId);
  const signature = affectedFatherDaughterSignature(ped, diseaseId);

  const observations = [];
  const limitations = [];

  // X5: without a reachable positive signature XLD topped out at "moderate",
  // so X-linked recessive out-ranked it on every pedigree regardless of the
  // evidence. An affected father transmitting to all his daughters and none of
  // his sons is the configuration that distinguishes them.
  if (signature)
    observations.push(
      obs(
        EVIDENCE_KEY.fatherToDaughters,
        `Affected male ${
          signature.pid
        } has affected daughter(s) (${signature.daughters.join(', ')})${
          signature.noAffectedSonsObserved
            ? ` and no affected son among ${signature.sons.length} recorded son(s)`
            : ''
        }`,
        signature
      )
    );

  if (chain >= 2)
    observations.push(
      obs(
        EVIDENCE_KEY.verticalChain,
        `Affected individuals in ${chain} successive generations (vertical transmission)`,
        { generations: chain }
      )
    );

  if (bothSexes)
    observations.push(
      obs(
        EVIDENCE_KEY.bothSexesAffected,
        `Both sexes affected (${affectedMales(ped, diseaseId).length} male, ${
          affectedFemales(ped, diseaseId).length
        } female)`
      )
    );

  if (m2mObs.observable)
    observations.push(
      obs(
        EVIDENCE_KEY.maleToMaleAbsent,
        `No male-to-male transmission, in a pedigree that could have shown it (${m2mObs.affectedMalesWithRecordedFather.length} affected male(s) with a recorded father, ${m2mObs.affectedMalesWithRecordedSons.length} with recorded sons)`
      )
    );
  else
    limitations.push(
      obs(
        EVIDENCE_KEY.maleToMaleNotObservable,
        'Male-to-male transmission could not have been observed here (no affected male has a recorded father or recorded sons), so its absence is not evidence'
      )
    );

  if (!bothSexes)
    limitations.push(
      obs(
        EVIDENCE_KEY.affectedFemalesPresent,
        'Affected individuals are all of one sex; X-linked dominant inheritance typically affects both'
      )
    );
  if (chain < 2)
    limitations.push(
      obs(
        EVIDENCE_KEY.noTransmission,
        'No parent-to-child transmission of this diagnosis is recorded'
      )
    );

  // The top tier requires the X-LINKED-SPECIFIC configuration, not simply a
  // long chain: an affected father whose daughters are all affected and whose
  // recorded sons are not. A multi-generation chain with both sexes affected is
  // the autosomal-dominant picture, so granting XLD its top tier for that would
  // just re-award AD's evidence to a different mode.
  //
  // Note the signature requires the father to have at least one recorded son
  // (`noAffectedSonsObserved`), which also makes male-to-male transmission
  // observable — so this tier can never rest on an unobservable absence.
  let confidence;
  const signatureStrong = Boolean(
    signature && signature.noAffectedSonsObserved && m2mObs.observable
  );
  if (bothSexes && chain >= 2 && signatureStrong)
    confidence = CONFIDENCE.strong;
  else if (bothSexes && chain >= 2) confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;

  const scale = scaleLimitation(affected, chain);
  if (scale) limitations.push(scale);

  return patternResult({
    id: PATTERN.XLD,
    diseaseId,
    confidence,
    supportingIds: pidsOf(affected),
    observations,
    limitations,
    observability: {
      maleToMale: m2mObs.observable ? 'absent_observable' : 'not_observable',
      fatherToAllDaughters: Boolean(signature),
      generationsSpanned: chain
    }
  });
}

// ---------------------------------------------------------------------------
// Mitochondrial
// ---------------------------------------------------------------------------
function detectMitochondrial(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.MT, diseaseId);

  // A sex-limited disease cannot support mitochondrial inference either. For a
  // female-limited disease, "no paternal transmission" is guaranteed (no
  // affected male can exist); for a male-limited one, the maternal-line
  // evidence it needs is impossible (no affected mother can exist). Either way
  // the criteria are facts about the disease, not the family.
  const limitedTo = sexLimitedTo(meta);
  if (limitedTo)
    return notAssertable(
      PATTERN.MT,
      diseaseId,
      NOT_ASSERTABLE.sexLimitedDisease,
      limitedTo === 'F'
        ? 'This diagnosis affects females only, so the absence of paternal transmission is guaranteed rather than observed'
        : 'This diagnosis affects males only, so maternal-line transmission cannot be observed'
    );

  const mol = diseaseAssociatedPathogenicGenes(ped, diseaseId, meta);

  // A pathogenic variant in a NUCLEAR gene associated with this disease, in an
  // affected individual, is incompatible with mitochondrial inheritance
  // whatever the (maternal-looking) topology. The association requirement is
  // new: any pathogenic gene in any affected person used to rule MT out for
  // every disease in the family.
  if (mol.genes.length)
    return patternResult({
      id: PATTERN.MT,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.nuclearVariant,
          `Pathogenic ${mol.genes.join(
            ', '
          )} variant (a nuclear gene associated with this diagnosis) in affected individual(s), which excludes mitochondrial inheritance`,
          { genes: mol.genes }
        )
      ],
      ruledOut: true,
      ruledOutReason: 'nuclear_pathogenic_variant'
    });

  if (hasPaternalTransmission(ped, diseaseId))
    return patternResult({
      id: PATTERN.MT,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.paternalPresent,
          'Paternal transmission observed, which excludes mitochondrial inheritance'
        )
      ],
      ruledOut: true,
      ruledOutReason: 'paternal_transmission'
    });

  const maternalPairs = maternalTransmissionPairs(ped, diseaseId);
  const paternalObs = paternalTransmissionObservability(ped, diseaseId);
  const observations = [];
  const limitations = [];

  // X2: "affected mother + one affected child" is two affected individuals and
  // one transmission, and it used to be enough for "strong". It is the same
  // picture as autosomal transmission through a mother.
  if (maternalPairs.length)
    observations.push(
      obs(
        EVIDENCE_KEY.maternalLine,
        `${
          maternalPairs.length
        } mother-to-child transmission(s) of this diagnosis (${maternalPairs
          .map((p) => `${p.parent} → ${p.child}`)
          .join('; ')})`,
        { pairs: maternalPairs }
      )
    );

  if (paternalObs.observable)
    observations.push(
      obs(
        EVIDENCE_KEY.paternalAbsent,
        `No paternal transmission, in a pedigree that could have shown it (${paternalObs.affectedFathersWithChildren.length} affected male(s) with recorded children, ${paternalObs.affectedWithRecordedFather.length} affected individual(s) with a recorded father)`
      )
    );
  else
    limitations.push(
      obs(
        EVIDENCE_KEY.paternalNotObservable,
        'Paternal transmission could not have been observed here (no affected male has a recorded child and no affected individual has a recorded father), so its absence is not evidence'
      )
    );

  if (maternalPairs.length === 1)
    limitations.push(
      obs(
        EVIDENCE_KEY.singleMaternalPair,
        'A single mother-to-child transmission: consistent with maternal inheritance, but equally with autosomal transmission through a mother'
      )
    );
  if (!maternalPairs.length)
    limitations.push(
      obs(
        EVIDENCE_KEY.noTransmission,
        'No maternal-line transmission of this diagnosis is recorded'
      )
    );

  let confidence;
  if (maternalPairs.length >= 2 && paternalObs.observable)
    confidence = CONFIDENCE.strong;
  else if (maternalPairs.length >= 1) confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;

  limitations.push(...molecularLimitations(mol));
  const scale = scaleLimitation(
    affected,
    affectedChainGenerations(ped, diseaseId)
  );
  if (scale) limitations.push(scale);

  return patternResult({
    id: PATTERN.MT,
    diseaseId,
    confidence,
    supportingIds: pidsOf(affected),
    observations,
    limitations,
    observability: {
      paternalTransmission: paternalObs.observable
        ? 'absent_observable'
        : 'not_observable',
      maternalTransmissions: maternalPairs.length
    }
  });
}

// ---------------------------------------------------------------------------
// Y-linked
// ---------------------------------------------------------------------------
function detectYLinked(ped, diseaseId, context) {
  const { meta } = toContext(context);
  const affected = affectedList(ped, diseaseId);
  if (affected.length === 0) return empty(PATTERN.YL, diseaseId);

  const limitedTo = sexLimitedTo(meta);
  if (limitedTo)
    return notAssertable(
      PATTERN.YL,
      diseaseId,
      NOT_ASSERTABLE.sexLimitedDisease,
      limitedTo === 'M'
        ? 'This diagnosis affects males only, so "only males affected" is guaranteed rather than observed'
        : 'This diagnosis affects females only, which is incompatible with Y-linked inheritance'
    );

  const females = affectedFemales(ped, diseaseId);
  if (females.length)
    return patternResult({
      id: PATTERN.YL,
      diseaseId,
      confidence: CONFIDENCE.ruledOut,
      supportingIds: pidsOf(affected),
      observations: [
        obs(
          EVIDENCE_KEY.affectedFemalesPresent,
          `${females.length} affected female(s) present, which excludes Y-linked inheritance`
        )
      ],
      ruledOut: true,
      ruledOutReason: 'affected_female'
    });

  const m2m = hasMaleToMaleTransmission(ped, diseaseId);
  const femaleObs = affectedFemaleObservability(ped, diseaseId);
  const observations = [];
  const limitations = [];

  if (m2m)
    observations.push(
      obs(EVIDENCE_KEY.maleToMalePresent, 'Father-to-son transmission observed')
    );

  if (femaleObs.observable)
    observations.push(
      obs(
        EVIDENCE_KEY.onlyMalesAffected,
        `Only males affected, among ${femaleObs.unaffectedFemaleRelatives.length} recorded female relative(s) of the affected individuals`
      )
    );
  else
    limitations.push(
      obs(
        EVIDENCE_KEY.affectedFemaleNotObservable,
        'No female relatives of the affected individuals are recorded, so "only males affected" describes who is on the pedigree rather than the disease'
      )
    );

  if (!m2m)
    limitations.push(
      obs(
        EVIDENCE_KEY.noTransmission,
        'No father-to-son transmission of this diagnosis is recorded'
      )
    );

  let confidence;
  if (m2m && affected.length >= 2 && femaleObs.observable)
    confidence = CONFIDENCE.strong;
  else if (m2m && affected.length >= 2) confidence = CONFIDENCE.moderate;
  else confidence = CONFIDENCE.weak;

  const scale = scaleLimitation(
    affected,
    affectedChainGenerations(ped, diseaseId)
  );
  if (scale) limitations.push(scale);

  return patternResult({
    id: PATTERN.YL,
    diseaseId,
    confidence,
    supportingIds: pidsOf(affected),
    observations,
    limitations,
    observability: {
      affectedFemale: femaleObs.observable
        ? 'absent_observable'
        : 'not_observable',
      maleToMale: m2m ? 'observed' : 'absent'
    }
  });
}

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/**
 * All six detectors for one disease.
 *
 * @param {object} ped tokenised pedigree
 * @param {string} diseaseId
 * @param {{ meta?: object|null, gateEnabled?: boolean }} [context] per-disease
 *   metadata and the `inheritanceCandidateGate` flag.
 * @returns {Array} pattern results — EMPTY when the gate denies a Mendelian
 *   mode for this disease. An empty array is the signal to `analysis/index.js`
 *   to emit familial aggregation instead; call `inheritanceAssertionGate` for
 *   the reason.
 */
export function detectInheritancePatterns(ped, diseaseId, context) {
  const ctx = toContext(context);
  if (!inheritanceAssertionGate(ctx.meta, ctx).allowed) return [];
  return [
    detectAutosomalDominant(ped, diseaseId, ctx),
    detectAutosomalRecessive(ped, diseaseId, ctx),
    detectXLinkedDominant(ped, diseaseId, ctx),
    detectXLinkedRecessive(ped, diseaseId, ctx),
    detectMitochondrial(ped, diseaseId, ctx),
    detectYLinked(ped, diseaseId, ctx)
  ];
}

// Best (highest-confidence, non-ruled-out) pattern for a disease — the
// per-disease inheritance signal Phenomizer Phase C and the cascade consume.
// Returns null when nothing fits, including when the gate denied a mode.
const RANK = { strong: 3, moderate: 2, weak: 1, ruled_out: 0, none: 0 };
export function bestInheritancePattern(ped, diseaseId, context) {
  const all = detectInheritancePatterns(ped, diseaseId, context);
  const viable = all.filter(
    (p) => p.assertable && !p.ruledOut && p.confidence !== CONFIDENCE.none
  );
  if (!viable.length) return null;
  // Rank, then break ties on detector order (AD, AR, XLD, XLR, MT, YL) so the
  // result is deterministic. Deliberately NOT on the number of observations: the
  // count of lines a detector emits is not a measure of evidential strength, and
  // ranking on it made a mode win by being wordier. XLD losing every tie to XLR
  // (X5) was a missing top tier, fixed in the detector itself, not a ranking
  // problem to paper over here.
  const order = new Map(all.map((p, i) => [p.id, i]));
  viable.sort(
    (a, b) =>
      RANK[b.confidence] - RANK[a.confidence] ||
      order.get(a.id) - order.get(b.id)
  );
  return viable[0];
}

export {
  detectAutosomalDominant,
  detectAutosomalRecessive,
  detectXLinkedRecessive,
  detectXLinkedDominant,
  detectMitochondrial,
  detectYLinked
};
